import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ClientAreaDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";

// Client area, ported from the prototype (erp-settings.js "/client-area"): account information, subscription and
// billing tabs. Account information and what the organization uses (active users against its seat limit, branches,
// warehouses, treasuries) are real. Plans, prices, renewal, the cart and payment are not built: they wait for the
// owner's pricing decision and a payment provider, so those parts say so instead of showing invented figures.

const TABS = [["info", "accountInfo"], ["subs", "subscriptions"], ["bills", "billingInvoices"]] as const;

export function ClientArea() {
  const { t, L } = useLang();
  const s = useSession();
  const [q, setQ] = useSearchParams();
  const tab = TABS.some(([k]) => k === q.get("tab")) ? q.get("tab")! : "info";
  const [data, setData] = useState<ClientAreaDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => { api<ClientAreaDto>("GET", "/client-area").then(setData, (e) => setFailed(errorText(t, e))); }, [t]);
  const o = data?.organization, u = data?.usage;
  const d = (x: string | undefined) => (x ? x : "--");
  const meter = (a: number, b: number) => (
    <><div className={`meter ${a >= b ? "bad" : a / b >= 0.8 ? "warn" : ""}`}><i style={{ width: `${Math.min(100, (a / b) * 100)}%` }} /></div><div className="hint num">{a} / {b}</div></>
  );
  const pending = <div className="dep-note" data-testid="billing-pending"><Icon name="lock" /><span>{t("billingPending")}</span></div>;
  let inner = null;
  if (failed) inner = <div className="state-box error"><Icon name="alert" /><h3>{failed}</h3></div>;
  else if (!o || !u) inner = <div className="state-box"><p>{t("loading")}</p></div>;
  else if (tab === "info") inner = (
    <div className="card-body"><dl className="ca-grid" data-testid="ca-info">
      {([["orgName", L(o)], ["phone", o.phone, 1], ["emailF", o.email, 1], ["fax", o.fax, 1], ["landline", o.landline, 1], ["taxNumber", o.taxNumber, 1], ["commercialRecord", o.commercialRecord, 1], ["country", o.country], ["city", o.city]] as [string, string, number?][])
        .map(([k, val, n]) => <div key={k}><dt>{t(k)}</dt><dd className={n ? "num" : ""}>{d(val)}</dd></div>)}
    </dl><div className="hint pad">{t("caInfoHint")} {s.can("setting") ? <Link to="/settings/organization">{t("settingsOrg")}</Link> : t("settingsOrg")}</div></div>
  );
  else if (tab === "subs") inner = (
    <div className="card-body">
      <div className="plan-now" data-testid="ca-usage">
        <div className="pn-main"><div className="pn-k">{t("currentPlan")}</div>
          <div className="pn-name">{t("planNotSet")} {o.status === "active" ? <span className="pill good">{t("activeF")}</span> : <span className="pill">{t("expired")}</span>}</div>
          <div className="pn-meta">{s.me?.package ? <>{t("caSeePackage")} <Link to="/settings/package" data-testid="ca-package-link">{t("setPackage")}</Link></> : t("planNotSetHint")}</div></div>
        <div className="pn-use">
          <div><div className="pn-k">{t("users")}</div>{meter(u.users, u.maxUsers)}</div>
          <div><div className="pn-k">{t("warehouses")}</div><div className="hint num" data-testid="ca-warehouses">{u.warehouses}</div></div>
        </div>
        <div className="pn-act"><span className="hint">{t("branches")}: <b className="num">{u.branches}</b> · {t("treasuries")}: <b className="num">{u.treasuries}</b></span></div>
      </div>
      <div style={{ marginTop: 14 }}>{pending}</div>
    </div>
  );
  else inner = <div className="card-body">{pending}<div className="state-box"><Icon name="receipt" /><h3>{t("noBills")}</h3></div></div>;
  return (
    <Shell navKey="/client-area">
      <PageHead crumbs={[[t("home"), "/home"], [t("clientArea")]]} title={t("clientArea")} />
      <section className="card list-card">
        <div className="tabs" role="tablist">{TABS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={`tab ${tab === k ? "on" : ""}`} onClick={() => setQ(k === "info" ? {} : { tab: k }, { replace: true })}>{t(l)}</button>
        ))}</div>
        {inner}
      </section>
    </Shell>
  );
}
